import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, Users, Globe } from 'lucide-react';
import BackLink from '../components/BackLink';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AgencyDashboardPage() {
  const [org, setOrg] = useState(null);
  const [domains, setDomains] = useState([]);
  const [branding, setBranding] = useState({});
  const [newDomain, setNewDomain] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API_URL}/api/orgs/mine`);
        setOrg(me.data);
        const b = await axios.get(`${API_URL}/api/orgs/${me.data.id}/branding`);
        setBranding(b.data);
      } catch (e) { setErr('No agency org linked.'); }
    })();
  }, []);

  if (err) return <div className="min-h-screen bg-midnight flex items-center justify-center text-text-light p-6">{err}</div>;
  if (!org) return <div className="min-h-screen bg-midnight flex items-center justify-center text-champagne">Loading…</div>;
  const isAgency = org.tier === 'agency';

  const upgrade = async () => {
    try { await axios.post(`${API_URL}/api/orgs/${org.id}/upgrade-to-agency`); setOrg({ ...org, tier: 'agency' }); }
    catch (e) { alert('Upgrade failed'); }
  };

  const addDomain = async () => {
    if (!newDomain) return;
    try { const r = await axios.post(`${API_URL}/api/orgs/${org.id}/custom-domains`, { domain: newDomain });
      setDomains([...domains, r.data]); setNewDomain(''); }
    catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  const invite = async () => {
    if (!inviteEmail) return;
    try { await axios.post(`${API_URL}/api/orgs/${org.id}/sub-users`, { email: inviteEmail, role: 'member' });
      alert('Invited'); setInviteEmail(''); }
    catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  const saveBranding = async () => {
    try { await axios.put(`${API_URL}/api/orgs/${org.id}/branding`, branding); alert('Saved'); }
    catch (e) { alert('Failed'); }
  };

  return (
    <div className="min-h-screen bg-midnight text-text-light">
      <SiteHeader />
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        <div className="mb-4"><BackLink to="/dashboard" label="Dashboard" /></div>
        <h1 className="font-serif text-3xl sm:text-4xl text-champagne mb-1 flex items-center gap-3"><Building2 className="w-8 h-8" />Agency</h1>
        <p className="text-text-muted mb-6">{org.name} · {org.tier} tier</p>

        {!isAgency && (
          <div className="bg-champagne/10 border border-champagne/30 rounded-xl p-5 mb-6">
            <div className="font-medium mb-2">Unlock Agency tier</div>
            <p className="text-sm text-text-muted mb-3">Unlimited sub-user seats · per-event custom domains · white-label workspace · Vault preservation included.</p>
            <button onClick={upgrade} className="bg-champagne text-midnight px-4 py-2 rounded font-medium">Upgrade to Agency</button>
          </div>
        )}

        <section className="bg-white/[0.03] border border-white/10 rounded-xl p-5 mb-6">
          <h2 className="text-lg font-medium mb-3 flex items-center gap-2"><Users className="w-5 h-5" />Sub-users</h2>
          <div className="flex gap-2">
            <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="user@example.com"
                   className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-2" aria-label="Invite email" />
            <button onClick={invite} className="bg-champagne text-midnight px-4 py-2 rounded">Invite</button>
          </div>
        </section>

        <section className="bg-white/[0.03] border border-white/10 rounded-xl p-5 mb-6">
          <h2 className="text-lg font-medium mb-3 flex items-center gap-2"><Globe className="w-5 h-5" />Custom domains</h2>
          <div className="flex gap-2 mb-3">
            <input value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="events.youragency.com"
                   className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-2" aria-label="Custom domain" />
            <button onClick={addDomain} className="bg-champagne text-midnight px-4 py-2 rounded">Add</button>
          </div>
          {domains.map(d => (
            <div key={d.id} className="text-sm text-text-muted">
              {d.domain} — set TXT: <code className="bg-white/5 px-2 py-1 rounded">{d.txt_record}</code>
            </div>
          ))}
        </section>

        <section className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
          <h2 className="text-lg font-medium mb-3">White-label branding</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Logo URL"><input value={branding.logo_url || ''} onChange={e => setBranding({ ...branding, logo_url: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 w-full" /></Field>
            <Field label="Primary color"><input value={branding.primary_color || ''} onChange={e => setBranding({ ...branding, primary_color: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 w-full" /></Field>
            <Field label="Accent color"><input value={branding.accent_color || ''} onChange={e => setBranding({ ...branding, accent_color: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 w-full" /></Field>
            <Field label="Footer text"><input value={branding.footer_text || ''} onChange={e => setBranding({ ...branding, footer_text: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 w-full" /></Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!branding.hide_memouraa_branding} onChange={e => setBranding({ ...branding, hide_memouraa_branding: e.target.checked })} />
              Hide “Powered by Memouraa” footer
            </label>
          </div>
          <button onClick={saveBranding} className="mt-4 bg-champagne text-midnight px-4 py-2 rounded font-medium">Save branding</button>
        </section>
      </div>
      <FooterExpanded />
    </div>
  );
}

function Field({ label, children }) {
  return <div><div className="text-xs text-text-muted mb-1">{label}</div>{children}</div>;
}
